import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  transporte as api,
  type ResultadoDoTeste,
  type ServicoDeTransporte,
  type SituacaoDoServico,
} from '../api/transporte'
import { ErroApi } from '../api/client'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { useSessao } from '../auth/useSessao'
import './times.css'

/**
 * As credenciais das transportadoras.
 *
 * 🔴 **O segredo nunca aparece aqui.** A tela mostra que está configurado, de
 * onde vem e os quatro últimos caracteres. Os identificadores — usuário, CNPJ,
 * CEP, cartão de postagem — aparecem inteiros de propósito: escondê-los
 * tornaria impossível achar um cadastro errado sem abrir o banco, e são
 * justamente eles que as pessoas erram.
 *
 * E **campo de segredo vazio não apaga**: o back preserva o que está guardado.
 *
 * A caixa de habilitação (059) é o único controle daqui cujo valor **não é
 * segredo** — é fato de operação, e por isso vai para a auditoria com o novo
 * estado. Ela só é enviada quando alguém a toca: mandá-la em todo *Salvar* faria
 * uma edição de URL ser recusada por causa de uma credencial incompleta que já
 * estava assim antes.
 */
export function Transportadoras() {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const [lista, setLista] = useState<SituacaoDoServico[] | null>(null)
  const [falhou, setFalhou] = useState(false)
  const [rascunho, setRascunho] = useState<Record<string, string>>({})
  const [salvando, setSalvando] = useState<string | null>(null)
  const [testando, setTestando] = useState<string | null>(null)
  const [teste, setTeste] = useState<Record<string, ResultadoDoTeste>>({})
  const [recado, setRecado] = useState<{ tom: 'sucesso' | 'perigo'; texto: string } | null>(null)

  const gerencia = pode('ParametroCtgf')

  const carregar = useCallback(async () => {
    setFalhou(false)
    try {
      setLista(await api.listar())
    } catch {
      setFalhou(true)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  function editar(servico: string, campo: string, valor: string) {
    setRascunho({ ...rascunho, [`${servico}.${campo}`]: valor })
  }

  function valorDe(s: SituacaoDoServico, campo: string) {
    const editado = rascunho[`${s.servico}.${campo}`]
    return editado ?? s.campos[campo] ?? ''
  }

  function habilitadoDe(s: SituacaoDoServico) {
    const editado = rascunho[`${s.servico}.habilitado`]
    return editado === undefined ? s.habilitado : editado === 'true'
  }

  async function salvar(s: SituacaoDoServico) {
    const campos: Record<string, string> = {}

    for (const campo of Object.keys(s.campos)) campos[campo] = valorDe(s, campo)
    for (const campo of Object.keys(s.segredos)) {
      const novo = rascunho[`${s.servico}.${campo}`]

      // Vazio significa "mantém o que está" — só mando o que foi digitado.
      if (novo) campos[campo] = novo
    }

    // Mesmo critério do segredo: só vai o que foi tocado. Assim editar a URL de
    // um serviço já ligado sem credencial completa não é recusado pela guarda.
    const flag = rascunho[`${s.servico}.habilitado`]
    if (flag !== undefined) campos.habilitado = flag

    setSalvando(s.servico)
    setRecado(null)
    try {
      await api.gravar(s.servico, campos)

      const limpo = { ...rascunho }
      for (const chave of Object.keys(limpo)) {
        if (chave.startsWith(`${s.servico}.`)) delete limpo[chave]
      }
      setRascunho(limpo)

      setRecado({
        tom: 'sucesso',
        texto: t('transporte.salvo', { servico: t(`transporte.nome.${s.servico}`) }),
      })
      await carregar()
    } catch (e) {
      // 🔴 A recusa da guarda nomeia os campos que faltam (`SEC-11`) — engolir a
      // falha aqui deixaria a caixa marcada na tela e desligada no banco.
      const corpo = e instanceof ErroApi ? (e.corpo as { detail?: string } | null) : null
      setRecado({ tom: 'perigo', texto: corpo?.detail ?? t('transporte.erroAoSalvar') })
    } finally {
      setSalvando(null)
    }
  }

  async function testar(servico: ServicoDeTransporte) {
    setTestando(servico)
    try {
      setTeste({ ...teste, [servico]: await api.testar(servico) })
    } finally {
      setTestando(null)
    }
  }

  return (
    <>
      <header className="times__topo">
        <div>
          <h1 className="times__titulo">{t('transporte.titulo')}</h1>
          <p className="times__descricao">{t('transporte.descricao')}</p>
        </div>
      </header>

      <Aviso tom="info">{t('transporte.nuncaVolta')}</Aviso>

      {recado && <Aviso tom={recado.tom}>{recado.texto}</Aviso>}

      {falhou ? (
        <Estado
          tipo="erro"
          titulo={t('transporte.erroAoCarregar')}
          acao={<Botao onClick={() => void carregar()}>{t('transporte.tentarDeNovo')}</Botao>}
        />
      ) : lista === null ? (
        <Estado tipo="carregando" titulo={t('transporte.carregando')} />
      ) : (
        <ul className="times__lista">
          {lista.map((s) => (
            <li key={s.servico}>
              <Cartao>
                <div className="times__cabecalho">
                  <h2 className="times__nome">
                    {t(`transporte.nome.${s.servico}`)}
                    <span className="times__ausente">
                      {!s.habilitado
                        ? t('transporte.desabilitado')
                        : s.configurado
                          ? t('transporte.configurado', {
                              fim: Object.values(s.segredos)[0] ?? '',
                              origem: t(`transporte.origem.${s.origem}`),
                            })
                          : t('transporte.naoConfigurado')}
                    </span>
                  </h2>
                </div>

                {/* O recuo pelo arquivo é dito em voz alta: quem opera precisa
                    saber que aquela credencial não está no cofre, e que
                    trocá-la ainda exige acesso à máquina. */}
                {s.origem === 'arquivo' && <Aviso tom="alerta">{t('transporte.vemDoArquivo')}</Aviso>}

                {teste[s.servico] && (
                  <Aviso tom={teste[s.servico].ok ? 'sucesso' : 'alerta'}>
                    {teste[s.servico].mensagem}
                  </Aviso>
                )}

                {gerencia && (
                  <div className="times__edicao">
                    <label className="times__opcao">
                      <input
                        type="checkbox"
                        checked={habilitadoDe(s)}
                        onChange={(e) =>
                          editar(s.servico, 'habilitado', String(e.target.checked))
                        }
                      />
                      <span>
                        {t('transporte.habilitada')}
                        <small>{t('transporte.habilitadaAjuda')}</small>
                      </span>
                    </label>

                    {Object.keys(s.campos).map((campo) => (
                      <Campo
                        key={campo}
                        rotulo={t(`transporte.campo.${campo}`)}
                        value={valorDe(s, campo)}
                        onChange={(e) => editar(s.servico, campo, e.target.value)}
                        ajuda={
                          t(`transporte.ajuda.${campo}`, { defaultValue: '' }) || undefined
                        }
                      />
                    ))}

                    {Object.keys(s.segredos).map((campo) => (
                      <Campo
                        key={campo}
                        type="password"
                        autoComplete="off"
                        rotulo={t(`transporte.campo.${campo}`)}
                        value={rascunho[`${s.servico}.${campo}`] ?? ''}
                        onChange={(e) => editar(s.servico, campo, e.target.value)}
                        ajuda={t('transporte.vazioMantem')}
                      />
                    ))}

                    <div className="times__acoes">
                      <Botao
                        variante="primario"
                        carregando={salvando === s.servico}
                        onClick={() => void salvar(s)}
                      >
                        {t('transporte.salvar')}
                      </Botao>

                      <Botao
                        variante="discreto"
                        carregando={testando === s.servico}
                        onClick={() => void testar(s.servico)}
                      >
                        {t('transporte.testar')}
                      </Botao>
                    </div>
                  </div>
                )}
              </Cartao>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}
